// Схлопывание однотипных уведомлений внутри временной секции списка.
//
// Правило схлопывания живёт в `@studyhub/shared/utils/aggregate-notifications`
// (окно 30 минут по `(type, groupKey)`) — здесь только перевод его результата в
// строки списка. Вынесено из `notification-list.tsx` отдельным модулем, чтобы
// шов «строка PowerSync → adaptNotification → строка списка» можно было
// покрыть тестом: именно он был разорван (адаптер выбрасывал `group_key`), а
// обе половины по отдельности работали и выглядели исправными.

import { aggregateNotifications } from "@studyhub/shared/utils/aggregate-notifications";
import type { NotificationItemData } from "./notification-item";

/**
 * Содержимое секции: либо одиночное уведомление, либо схлопнутая серия.
 * Отдельный тип от строки виртуализации нужен, чтобы невиртуализированная
 * ветка знала, что заголовков среди строк секции не бывает.
 */
export type ContentRow =
	| { kind: "item"; item: NotificationItemData }
	| { kind: "cluster"; id: string; items: NotificationItemData[] };

/**
 * Считается ПОСЛЕ разбивки по датам намеренно: секции («Сегодня», «Вчера») —
 * то, что видит пользователь, и группа, перешагнувшая заголовок, выглядела бы
 * как строка не из своего дня. Окно агрегатора (30 мин) заведомо короче суток,
 * так что реально теряется только серия, легшая ровно на полночь.
 *
 * Вход обязан идти DESC по `createdAt` — его даёт `useNotificationsListQuery`
 * (`ORDER BY created_at DESC`), а разбивка по датам порядок не меняет.
 *
 * Серия из одного элемента возвращается обычной строкой, а не группой: карточка
 * со счётчиком «1» и стрелкой раскрытия — хуже простой строки.
 */
export function clusterize(items: NotificationItemData[]): ContentRow[] {
	return aggregateNotifications(items).map((group) =>
		group.count > 1
			? { kind: "cluster" as const, id: group.representative.id, items: group.items }
			: { kind: "item" as const, item: group.representative },
	);
}

/** Число уведомлений в секции (а не строк): свёрнутая серия из трёх — это три. */
export function countNotifications(rows: ContentRow[]): number {
	return rows.reduce((sum, row) => sum + (row.kind === "cluster" ? row.items.length : 1), 0);
}
